import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { applyUpdate, onUpdateAvailable, registerSW } from '../lib/sw';
import { useLang } from '../contexts/LanguageContext';

/**
 * Shows a small toast-style banner when a new app version has been downloaded
 * but is waiting for the user to accept the update. Clicking "Update" triggers
 * SKIP_WAITING in the service worker and reloads the page.
 */
export default function UpdatePrompt() {
  const { t } = useLang();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    registerSW();
    const off = onUpdateAvailable(() => setShow(true));
    return off;
  }, []);

  if (!show || dismissed) return null;

  return (
    <div
      data-testid="update-prompt"
      className="fixed bottom-24 inset-x-4 sm:inset-x-auto sm:end-6 sm:max-w-sm z-[150] rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-neutral-900 shadow-2xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#C05A42] text-white">
        <RefreshCw className="h-5 w-5" />
      </span>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-[#2C2420] dark:text-neutral-100">
          {t('updateAvailable')}
        </h3>
        <p className="text-xs text-[#5C4D44] dark:text-neutral-400 mt-0.5">
          {t('updateAvailableHint')}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={applyUpdate}
            data-testid="update-now-btn"
            className="inline-flex items-center gap-1 rounded-full bg-[#C05A42] hover:bg-[#A04832] text-white px-4 py-1.5 text-xs font-medium transition"
          >
            <RefreshCw className="h-3 w-3" />
            {t('updateNow')}
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            data-testid="update-later-btn"
            className="rounded-full px-3 py-1.5 text-xs text-[#5C4D44] dark:text-neutral-400 hover:bg-[#EFEBE1] dark:hover:bg-neutral-800 transition"
          >
            {t('updateLater')}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('exit')}
        className="text-[#5C4D44] dark:text-neutral-400 hover:text-[#2C2420] dark:hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
